import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useHistory, withRouter } from 'react-router';

import { observer } from 'mobx-react-lite';

import {
	BUTTON_TYPES,
	EvFilters,
	EvTitle,
	IFilterValues,
	TFilterOptions
} from '@evinced-private/ui-common';

import {
	MANAGE_COLLECTIONS_FILTERS_ID,
	MANAGE_COLLECTIONS_TABLE_ID
} from '../../consts/PropertyCollection';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { useGetAllProperties } from '../../hooks/queries/useGetAllProperties';
import { useCommonFiltersStore } from '../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { useTablesStore } from '../../providers/tablesProvider/TablesProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';

import { EditCollectionButton } from './components/edit-collection-button/EditCollectionButton';
import { ManageCollectionsTable } from './components/manage-collections-table/ManageCollectionsTable';
import {
	filterDefinitions,
	getInitialFilterOptions,
	getInitialFilterValues
} from './helpers/ManageCollectionsFilterHelper';

import './ManageCollectionsPage.scss';

const titleDescriptionId = 'title-description';

const ManageCollectionsPage: FC = () => {
	const commonFiltersStore = useCommonFiltersStore();
	const tablesStore = useTablesStore();
	const history = useHistory();
	const { getToggle } = useConfiguration();

	const { data: properties = [], isLoading: isPropertiesLoading } = useGetAllProperties(
		[],
		false,
		false
	);

	const isMultipleCollectionsEnabled =
		getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS) || false;

	const appliedFilters = commonFiltersStore.getFilterValue(MANAGE_COLLECTIONS_FILTERS_ID);

	const filterValues: IFilterValues = useMemo(() => {
		const initialFilterValues = getInitialFilterValues(commonFiltersStore);
		return {
			text: { searchValue: appliedFilters?.searchValue || initialFilterValues.text.searchValue },
			select: {
				collectionTypes:
					appliedFilters?.collectionTypes || initialFilterValues.select.collectionTypes
			}
		};
	}, [appliedFilters, commonFiltersStore]);
	const { sizePerPage } = tablesStore.getPagination(MANAGE_COLLECTIONS_TABLE_ID);

	const filterOptions: TFilterOptions = useMemo(() => getInitialFilterOptions(), []);

	const handleApplyFilters = useCallback(
		(appliedFilters: IFilterValues): void => {
			const flatFilters = { ...appliedFilters.text, ...appliedFilters.select };
			commonFiltersStore.updateSelectedFilters(MANAGE_COLLECTIONS_FILTERS_ID, flatFilters);
			tablesStore.updatePagination(MANAGE_COLLECTIONS_TABLE_ID, { page: 1, sizePerPage });
		},
		[commonFiltersStore, sizePerPage, tablesStore]
	);

	// should be removed when multiple collections feature is fully integrated
	useEffect(() => {
		if (!isMultipleCollectionsEnabled) {
			history.push(RoutesHelper.getHomepagePath());
		}
	}, [isMultipleCollectionsEnabled, history]);

	// set initial filter state
	useEffect(() => {
		if (!appliedFilters) {
			commonFiltersStore.updateSelectedFilters(MANAGE_COLLECTIONS_FILTERS_ID, {
				searchValue: '',
				collectionTypes: []
			});
		}
	}, [appliedFilters, commonFiltersStore]);

	return (
		<div className="manage-collections-root">
			<div className="manage-collections-header">
				<div className="manage-collections-title">
					<EvTitle
						headingLevel={1}
						titleText="Manage Collections"
						id="manage-collections"
						descriptionId={titleDescriptionId}
					/>
					<span id={titleDescriptionId} className="title-description">
						Group properties into collections for easier tracking.
					</span>
				</div>
				<EditCollectionButton
					properties={properties}
					type={BUTTON_TYPES.PRIMARY}
					buttonChildren="Create Collection"
					loading={isPropertiesLoading}
				/>
			</div>
			<EvFilters
				onApply={handleApplyFilters}
				onReset={handleApplyFilters}
				initialValues={filterValues}
				filterOptions={filterOptions}
				isFilterOptionsLoaded={true}
				filterDefinitions={filterDefinitions}
			/>
			<ManageCollectionsTable properties={properties} isPropertiesLoading={isPropertiesLoading} />
		</div>
	);
};

export const ManageCollectionsPageWithRouter = withRouter(observer(ManageCollectionsPage));
